/**
 * The rule builder's state: a pure reducer over `RuleDraft`. Every edit the
 * dialog can make is one `Action`, so the rules below are testable without a
 * render. The reducer never mutates its input.
 */
import {
  ACTION_ORDER,
  FIELDS,
  MAX_ACTIONS,
  isListOp,
  type ActionKey,
  type ConditionDraft,
  type Direction,
  type FieldKey,
  type GroupDraft,
  type OpKey,
  type RuleDraft,
} from './ruleWire'

export type Action =
  | { t: 'name'; v: string }
  | { t: 'enabled'; v: boolean }
  | { t: 'addGroup' }
  | { t: 'removeGroup'; g: number }
  | { t: 'groupJoin'; g: number; v: 'and' | 'or' }
  | { t: 'addCond'; g: number }
  | { t: 'removeCond'; g: number; c: number }
  | { t: 'condField'; g: number; c: number; v: FieldKey }
  | { t: 'condOp'; g: number; c: number; v: OpKey }
  | { t: 'condDirection'; g: number; c: number; v: Direction }
  | { t: 'condValue'; g: number; c: number; v: string; second?: boolean }
  | { t: 'condList'; g: number; c: number; v: string[] }
  | { t: 'addAction' }
  | { t: 'removeAction'; i: number }
  | { t: 'actionType'; i: number; v: ActionKey }
  | { t: 'actionValue'; i: number; v: string }
  | { t: 'applyToPast'; v: boolean }
  | { t: 'backfillStart'; v: string | null }

const FIRST_FIELD: FieldKey = 'merchant_name'

function blankCondition(field: FieldKey = FIRST_FIELD): ConditionDraft {
  // `ops` is a non-empty literal tuple for every field; `contains` is only the
  // unreachable fallback `noUncheckedIndexedAccess` asks for.
  const op = FIELDS[field].ops[0] ?? 'contains'
  // Only an amount has a direction; money out is the default (plan #517).
  if (field === 'amount') return { field, op, value: '', value2: '', list: [], direction: 'spent' }
  return { field, op, value: '', value2: '', list: [] }
}

const blankGroup = (): GroupDraft => ({ join: 'and', conditions: [blankCondition()] })

export function blankDraft(): RuleDraft {
  return {
    id: null,
    name: '',
    enabled: true,
    groups: [blankGroup()],
    actions: [{ type: ACTION_ORDER[0] ?? 'set_category', value: '' }],
    applyToPast: false,
    backfillStart: null,
  }
}

/** Replace item `i` of `xs` via `f`; out of range returns `xs` unchanged. */
function at<T>(xs: T[], i: number, f: (x: T) => T): T[] {
  const x = xs[i]
  if (x === undefined) return xs
  const next = [...xs]
  next[i] = f(x)
  return next
}

function onCond(d: RuleDraft, g: number, c: number, f: (x: ConditionDraft) => ConditionDraft): RuleDraft {
  const groups = at(d.groups, g, (grp) => {
    const conditions = at(grp.conditions, c, f)
    return conditions === grp.conditions ? grp : { ...grp, conditions }
  })
  return groups === d.groups ? d : { ...d, groups }
}

export function reduce(d: RuleDraft, a: Action): RuleDraft {
  switch (a.t) {
    case 'name':
      return { ...d, name: a.v }
    case 'enabled':
      return { ...d, enabled: a.v }
    case 'addGroup':
      return { ...d, groups: [...d.groups, blankGroup()] }
    case 'removeGroup': {
      if (d.groups[a.g] === undefined) return d
      const groups = d.groups.filter((_, i) => i !== a.g)
      return { ...d, groups: groups.length > 0 ? groups : [blankGroup()] }
    }
    case 'groupJoin': {
      const groups = at(d.groups, a.g, (grp) => ({ ...grp, join: a.v }))
      return groups === d.groups ? d : { ...d, groups }
    }
    case 'addCond': {
      const groups = at(d.groups, a.g, (grp) => ({
        ...grp,
        conditions: [...grp.conditions, blankCondition()],
      }))
      return groups === d.groups ? d : { ...d, groups }
    }
    case 'removeCond': {
      const grp = d.groups[a.g]
      if (grp === undefined || grp.conditions[a.c] === undefined) return d
      const conditions = grp.conditions.filter((_, i) => i !== a.c)
      if (conditions.length > 0) {
        return { ...d, groups: at(d.groups, a.g, () => ({ ...grp, conditions })) }
      }
      // Last condition: the group goes, unless it is the only one — then it
      // stays with one blank condition, so the builder always has a row.
      if (d.groups.length > 1) return { ...d, groups: d.groups.filter((_, i) => i !== a.g) }
      return { ...d, groups: [{ ...grp, conditions: [blankCondition()] }] }
    }
    case 'condField':
      // Everything but the field resets: a `between` from a date must not
      // survive into a text field, nor its values.
      return onCond(d, a.g, a.c, () => blankCondition(a.v))
    case 'condOp':
      // The list and the second value belong to `is_in` / `between`; a
      // switch away from either drops what the new op cannot send.
      return onCond(d, a.g, a.c, (c) => ({
        ...c,
        op: a.v,
        value2: a.v === 'between' ? c.value2 : '',
        list: isListOp(a.v) ? c.list : [],
      }))
    case 'condDirection':
      return onCond(d, a.g, a.c, (c) => (c.field === 'amount' ? { ...c, direction: a.v } : c))
    case 'condValue':
      return onCond(d, a.g, a.c, (c) => (a.second === true ? { ...c, value2: a.v } : { ...c, value: a.v }))
    case 'condList':
      return onCond(d, a.g, a.c, (c) => ({ ...c, list: a.v }))
    case 'addAction': {
      if (d.actions.length >= MAX_ACTIONS) return d
      const used = new Set(d.actions.map((x) => x.type))
      const free = ACTION_ORDER.find((k) => !used.has(k))
      if (free === undefined) return d
      return { ...d, actions: [...d.actions, { type: free, value: '' }] }
    }
    case 'removeAction': {
      if (d.actions[a.i] === undefined) return d
      return { ...d, actions: d.actions.filter((_, i) => i !== a.i) }
    }
    case 'actionType': {
      const actions = at(d.actions, a.i, () => ({ type: a.v, value: '' }))
      return actions === d.actions ? d : { ...d, actions }
    }
    case 'actionValue': {
      const actions = at(d.actions, a.i, (x) => ({ ...x, value: a.v }))
      return actions === d.actions ? d : { ...d, actions }
    }
    case 'applyToPast':
      // `backfillStart` is kept, so toggling back on restores the user's date.
      return { ...d, applyToPast: a.v }
    case 'backfillStart':
      return { ...d, backfillStart: a.v }
  }
}

const PRIORITY_MIN = 1
const PRIORITY_MAX = 1000
const PRIORITY_DEFAULT = 100

/** The schema gives priorities 1…1000 and the backend has no count cap, so
 *  1000 is the most rules that can each hold a distinct priority. */
export const MAX_RULES = PRIORITY_MAX

/**
 * "Top" needs the list renumbered when the highest rule already sits at the
 * ceiling: the new rule would tie it, and a tie falls to `created_at`, which
 * puts the newer rule SECOND. "Bottom" never needs it — a tie at 1 loses the
 * same way, so the new rule is last.
 */
export function needsRenumber(place: 'top' | 'bottom', existing: readonly number[]): boolean {
  return place === 'top' && existing.length > 0 && Math.max(...existing) >= PRIORITY_MAX
}

/**
 * A new rule's priority from where the user placed it. The backend runs the
 * HIGHEST priority first, so "top" is one above the current maximum and
 * "bottom" one below the minimum — each clamped to the schema's 1…1000.
 */
export function priorityFor(place: 'top' | 'bottom', existing: readonly number[]): number {
  if (existing.length === 0) return PRIORITY_DEFAULT
  return place === 'top'
    ? Math.min(PRIORITY_MAX, Math.max(...existing) + 1)
    : Math.max(PRIORITY_MIN, Math.min(...existing) - 1)
}

/** What "Create" on a suggestion opens the builder with. `applyToPast` is on:
 *  the suggestion is about transactions that already exist, and the save still
 *  counts and asks before writing any of them. */
export function suggestionDraft(s: { pattern: string; categoryId: string }): RuleDraft {
  return {
    ...blankDraft(),
    groups: [
      {
        join: 'and',
        conditions: [{ field: 'merchant_name', op: 'contains', value: s.pattern, value2: '', list: [] }],
      },
    ],
    actions: [{ type: 'set_category', value: s.categoryId }],
    applyToPast: true,
  }
}

/**
 * A rule over a set of merchants, for M6's bulk "Create rule from these".
 *
 * One merchant is `equals`, several are `is_in` over the list. The field is
 * `merchant_name`: the rule engine matches it against the name the user SEES
 * (display name, then merchant name, then the raw descriptor), which is the
 * `merchant` a transaction row carries. The raw bank string is a different field,
 * `merchant_name_raw`, and matching a displayed name against it finds nothing.
 *
 * `categoryId` null means the rows disagree (or have none): the action is left
 * blank, so the rule is not savable until the user chooses a category.
 */
export function merchantsDraft(merchants: string[], categoryId: string | null): RuleDraft {
  return {
    ...blankDraft(),
    groups: [
      {
        join: 'and',
        conditions: [
          {
            field: 'merchant_name',
            op: merchants.length === 1 ? 'equals' : 'is_in',
            value: merchants[0] ?? '',
            value2: '',
            list: merchants,
          },
        ],
      },
    ],
    actions: [{ type: 'set_category', value: categoryId ?? '' }],
    applyToPast: true,
  }
}
